import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, Badge, ActionBtn, SectionHeader, FormField, ProgressBar, Pagination } from '../components/common/UI';
import { VIDEO_STATUS } from '../utils/statusHelpers';
import { toShamsi, daysUntil } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { MdAdd, MdEdit, MdDelete, MdCheckCircle, MdRefresh } from '../utils/icons';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';
import { 
  MdApps, 
  MdVideoCameraBack, 
  MdRemoveRedEye, 
  MdAutorenew 
} from 'react-icons/md';

import { FaCheckCircle } from 'react-icons/fa';

const initialForm = { project: '', title: '', editor: '', status: 'Editing', progress: 0, version: 'v1', deadline: '', clientComment: '', revisionReason: '', notes: '' };

export default function Videos() {
  const { isManager } = useAuth();
  const [videos, setVideos] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const load = useCallback(async () => {
    try {
      const [vidRes, empRes] = await Promise.all([
        API.get('/videos' + (filterStatus ? `?status=${filterStatus}` : '')),
        API.get('/employees'),
      ]);
      setVideos(vidRes.data);
      setEmployees(empRes.data.filter(e => e.role === 'Editor'));
    } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const tabFiltered = videos.filter(v => {
    if (activeTab === 'editing') return v.status === 'Editing';
    if (activeTab === 'review') return v.status === 'Review';
    if (activeTab === 'revision') return v.status === 'Revision';
    if (activeTab === 'done') return ['Approved', 'Delivered'].includes(v.status);
    return true;
  });

  // پیج‌بندی (صفحه‌بندی سمت کلاینت)
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [activeTab, filterStatus]);
  const totalPages = Math.max(1, Math.ceil(tabFiltered.length / PAGE_SIZE));
  const paged = tabFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd = () => { setForm(initialForm); setEditItem(null); setShowModal(true); };
  const openEdit = (v) => {
    setForm({ project: v.project?._id || '', title: v.title, editor: v.editor?._id || '', status: v.status, progress: v.progress, version: v.version, deadline: v.deadline?.split('T')[0] || '', clientComment: v.clientComment || '', revisionReason: v.revisionReason || '', notes: v.notes || '' });
    setEditItem(v); setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title) return toast.error('عنوان ویدیو ضروری است');
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.project) delete payload.project;
      if (editItem) { await API.put(`/videos/${editItem._id}`, payload); toast.success('ویدیو بروزرسانی شد'); }
      else { await API.post('/videos', payload); toast.success('ویدیو اضافه شد'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'خطا'); } finally { setSaving(false); }
  };

  const quickApprove = async (id) => {
    try {
      await API.put(`/videos/${id}`, { clientApproved: true, status: 'Approved' });
      toast.success('ویدیو تأیید شد');
      load();
    } catch { toast.error('خطا'); }
  };

  const quickRevision = async (id) => {
    try {
      await API.put(`/videos/${id}`, { status: 'Revision' });
      toast.success('وضعیت به اصلاح تغییر کرد');
      load();
    } catch { toast.error('خطا'); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/videos/${deleteId}`); toast.success('ویدیو حذف شد'); setDeleteId(null); load(); }
    catch { toast.error('خطا'); }
  };

  if (loading) return <PageLoader />;

  const tabs = [
    { key: 'all', label: 'همه', count: videos.length, icon: <MdApps /> },
    { key: 'editing', label: 'در ادیت', count: videos.filter(v => v.status === 'Editing').length, icon: <MdVideoCameraBack /> },
    { key: 'review', label: 'بررسی', count: videos.filter(v => v.status === 'Review').length, icon: <MdRemoveRedEye /> },
    { key: 'revision', label: 'اصلاح', count: videos.filter(v => v.status === 'Revision').length, icon: <MdAutorenew /> },
    { key: 'done', label: 'تأیید شده', count: videos.filter(v => ['Approved', 'Delivered'].includes(v.status)).length, icon: <FaCheckCircle /> },
  ];

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader title="ویدیوها" subtitle={`${videos.length} ویدیو`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="ویدیو جدید" />} />

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}>
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-md ${activeTab === tab.key ? 'bg-gray-200' : 'bg-gray-100'}`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {tabFiltered.length === 0 ? <EmptyState icon="🎬" title="ویدیویی یافت نشد" action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="ویدیو جدید" />} /> : (
        <>
          {/* Desktop Table */}
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>عنوان</th>
                    <th>پروژه</th>
                    <th>ادیتور</th>
                    <th>پیشرفت</th>
                    <th>نسخه</th>
                    <th>دیدلاین</th>
                    <th>وضعیت</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(v => (
                    <tr key={v._id}>
                      <td>
                        <div className="font-semibold text-gray-800">{v.title}</div>
                        {v.clientComment && <div className="text-xs text-orange-600 mt-0.5">💬 {v.clientComment.slice(0, 40)}...</div>}
                      </td>
                      <td className="text-gray-600">{v.project?.name || '---'}</td>
                      <td className="text-gray-600">{v.editor?.name || '---'}</td>
                      <td className="w-36">
                        <div className="flex items-center gap-2">
                          <div className="flex-1"><ProgressBar value={v.progress} color={v.progress === 100 ? 'green' : 'blue'} /></div>
                          <span className="text-xs text-gray-500 w-8">{v.progress}%</span>
                        </div>
                      </td>
                      <td><span className="badge bg-blue-100 text-blue-700 border border-blue-200">{v.version}</span></td>
                      <td>
                        {v.deadline ? (
                          <div>
                            <div className="text-gray-600 text-sm">{toShamsi(v.deadline)}</div>
                            {v.status !== 'Approved' && v.status !== 'Delivered' && (
                              <div className={`text-xs ${daysUntil(v.deadline) < 0 ? 'text-red-600' : daysUntil(v.deadline) <= 2 ? 'text-yellow-600' : 'text-gray-600'}`}>
                                {daysUntil(v.deadline) < 0 ? `${Math.abs(daysUntil(v.deadline))} روز تأخیر` : `${daysUntil(v.deadline)} روز`}
                              </div>
                            )}
                          </div>
                        ) : '---'}
                      </td>
                      <td><Badge statusConfig={VIDEO_STATUS[v.status]} /></td>
                      <td>
                        <div className="flex items-center gap-1">
                          {isManager && v.status === 'Review' && (
                            <>
                              <button onClick={() => quickApprove(v._id)} className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-all" title="تأیید"><MdCheckCircle size={15} /></button>
                              <button onClick={() => quickRevision(v._id)} className="p-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-100 rounded-lg transition-all" title="اصلاح"><MdRefresh size={15} /></button>
                            </>
                          )}
                          {isManager && <button onClick={() => openEdit(v)} className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all"><MdEdit size={15} /></button>}
                          {isManager && <button onClick={() => setDeleteId(v._id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"><MdDelete size={15} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {paged.map(v => (
              <div key={v._id} className="mobile-card">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-gray-800">{v.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{v.project?.name} • {v.editor?.name || 'ادیتور ندارد'}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge statusConfig={VIDEO_STATUS[v.status]} />
                    <span className="text-xs text-blue-600">{v.version}</span>
                  </div>
                </div>
                <ProgressBar value={v.progress} label={`پیشرفت: ${v.progress}%`} color={v.progress === 100 ? 'green' : 'blue'} />
                {v.clientComment && <div className="text-xs text-orange-600 mt-2">💬 {v.clientComment}</div>}
                {isManager && v.status === 'Review' && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => quickApprove(v._id)} className="flex-1 py-1.5 text-xs bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors">✅ تأیید</button>
                    <button onClick={() => quickRevision(v._id)} className="flex-1 py-1.5 text-xs bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors">🔁 اصلاح</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش ویدیو' : 'ویدیو جدید'} size="md">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="عنوان ویدیو" required>
            <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="عنوان ویدیو" />
          </FormField>
          <FormField label="ادیتور">
            <select className="form-input" value={form.editor} onChange={e => setForm({ ...form, editor: e.target.value })}>
              <option value="">انتخاب ادیتور</option>
              {employees.map(e => <option key={e._id} value={e._id}>{e.name}</option>)}
            </select>
          </FormField>
          <FormField label="وضعیت">
            <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {Object.entries(VIDEO_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </FormField>
          <FormField label="استایل">
            <input className="form-input" onChange={e => setForm({ ...form, version: e.target.value })} placeholder="استایل" />
          </FormField>
          <FormField label="دیدلاین">
          <ShamsiDatePicker
  value={form.deadline}
  onChange={(gregorianDate) => setForm({ ...form, deadline: gregorianDate })}
  className="form-input w-full"
  placeholder="انتخاب تاریخ"
/>
          </FormField>
          <div className="sm:col-span-2">
            <FormField label={`پیشرفت: ${form.progress}%`}>
              <input type="range" min="0" max="100" value={form.progress} onChange={e => setForm({ ...form, progress: +e.target.value })}
                className="w-full accent-blue-500" />
            </FormField>
          </div>
          {form.status === 'Revision' && (
            <div className="sm:col-span-2">
              <FormField label="دلیل اصلاح">
                <textarea className="form-input h-20 resize-none" value={form.revisionReason} onChange={e => setForm({ ...form, revisionReason: e.target.value })} placeholder="توضیح دهید چه چیزی باید اصلاح شود..." />
              </FormField>
            </div>
          )}
          <div className="sm:col-span-2">
            <FormField label="کامنت مشتری">
              <input className="form-input" value={form.clientComment} onChange={e => setForm({ ...form, clientComment: e.target.value })} placeholder="کامنت یا فیدبک مشتری" />
            </FormField>
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
          <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">انصراف</button>
          <ActionBtn onClick={handleSave} label={saving ? 'ذخیره...' : editItem ? 'بروزرسانی' : 'ذخیره'} disabled={saving} />
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف ویدیو" message="آیا مطمئن هستید؟" />
    </div>
  );
}
